import React, { useState } from 'react';
import { Play, X, ChevronLeft, ChevronRight } from 'lucide-react';

// Featured Spotlight Content - YouTube videos
// uwVVbpkWuLU is the preset featured (first)
const spotlightContent = [
  {
    id: 1,
    videoId: 'uwVVbpkWuLU',
    title: "Featured Cinematic Spotlight",
    description: "Highlighted work from Augmented Cinematics.",
    type: 'youtube'
  },
  {
    id: 2,
    videoId: 'uzem6ErOcIE',
    title: "Cinematic Spotlight",
    description: "Highlighted work from Augmented Cinematics.",
    type: 'youtube'
  },
  {
    id: 3,
    videoId: 'URiNzgkSqpA',
    title: "White Men Make the Best Lovers",
    description: "Highlighted work from Augmented Cinematics.",
    type: 'youtube'
  },
  {
    id: 4,
    videoId: 'Em9mE8Ypb8k',
    title: "The Bear's Aggression",
    description: "Highlighted work from Augmented Cinematics.",
    type: 'youtube'
  },
  {
    id: 5,
    videoId: 'lNISgiD4TIw',
    title: "Before You Decide Remember",
    description: "Highlighted work from Augmented Cinematics.",
    type: 'youtube'
  },
  {
    id: 6,
    videoId: 'JVSYVzWzGr4',
    title: "Cinematic Scene Reel",
    description: "Highlighted work from Augmented Cinematics.",
    type: 'youtube'
  }
];

const FeaturedSpotlightSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % spotlightContent.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + spotlightContent.length) % spotlightContent.length);
  };

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const getYouTubeEmbed = (videoId) => {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  };

  const openVideo = (item) => {
    setSelectedVideo(item);
  };

  const closeModal = () => {
    setSelectedVideo(null);
  };

  // Get indices for visible items (prev, current, next)
  const getPrevIndex = () => (currentIndex - 1 + spotlightContent.length) % spotlightContent.length;
  const getNextIndex = () => (currentIndex + 1) % spotlightContent.length;

  const prevItem = spotlightContent[getPrevIndex()];
  const currentItem = spotlightContent[currentIndex];
  const nextItem = spotlightContent[getNextIndex()];

  return (
    <section className="py-16 px-4 bg-[#040c14] relative overflow-hidden">
      {/* Abstract background elements */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="abstract-blob animate-float" style={{ 
          top: '20%', 
          left: '10%', 
          width: '500px', 
          height: '500px', 
          background: 'radial-gradient(circle, rgba(255, 101, 0, 0.3), transparent 70%)',
          filter: 'blur(90px)'
        }}></div>
        <div className="abstract-blob animate-float-delayed" style={{ 
          bottom: '10%', 
          right: '15%', 
          width: '400px', 
          height: '400px', 
          background: 'radial-gradient(circle, rgba(255, 140, 50, 0.2), transparent 70%)',
          filter: 'blur(85px)'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 text-center">
          Cinematics <span className="text-[#FF6500]">Featured Spotlight</span>
        </h2>
        <p className="text-lg text-[#aaa] text-center mb-10 max-w-2xl mx-auto">
          {currentItem.description}
        </p>

        {/* Moving Gallery Carousel - Shows prev, current, next */}
        <div className="relative flex items-center justify-center gap-4 md:gap-6">
          
          {/* Previous Video (smaller, left side) */}
          <div 
            className="hidden md:block relative w-48 lg:w-64 aspect-video rounded-lg overflow-hidden border border-[#152840] opacity-50 hover:opacity-80 cursor-pointer transition-all duration-300 flex-shrink-0"
            onClick={goToPrev}
          >
            <img 
              src={getYouTubeThumbnail(prevItem.videoId)}
              alt={prevItem.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = `https://img.youtube.com/vi/${prevItem.videoId}/hqdefault.jpg`;
              }}
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <ChevronLeft className="w-10 h-10 text-white/70" />
            </div>
          </div>

          {/* Navigation Button - Left (mobile) */}
          <button
            onClick={goToPrev}
            className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-[#0a1525]/90 hover:bg-[#FF6500] border border-[#152840] hover:border-[#FF6500] rounded-full flex items-center justify-center transition-all duration-300 group"
            aria-label="Previous spotlight"
          >
            <ChevronLeft className="w-5 h-5 text-white group-hover:text-black" />
          </button>

          {/* Current Video (main, center) */}
          <div 
            className="relative w-full max-w-3xl aspect-video rounded-xl overflow-hidden border-2 border-[#FF6500]/30 hover:border-[#FF6500] transition-all duration-300 shadow-2xl shadow-[#FF6500]/20 cursor-pointer group"
            onClick={() => openVideo(currentItem)}
          >
            <img 
              src={getYouTubeThumbnail(currentItem.videoId)}
              alt={currentItem.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                e.target.src = `https://img.youtube.com/vi/${currentItem.videoId}/hqdefault.jpg`;
              }}
            />
            {/* Play button overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-center justify-center">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-[#FF6500] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl shadow-[#FF6500]/50">
                <Play className="w-10 h-10 md:w-12 md:h-12 text-black ml-1" />
              </div>
            </div>
            {/* Title overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/90 to-transparent">
              <p className="text-white text-lg md:text-2xl font-semibold">{currentItem.title}</p>
            </div>
          </div>

          {/* Navigation Button - Right (mobile) */}
          <button
            onClick={goToNext}
            className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-[#0a1525]/90 hover:bg-[#FF6500] border border-[#152840] hover:border-[#FF6500] rounded-full flex items-center justify-center transition-all duration-300 group"
            aria-label="Next spotlight"
          >
            <ChevronRight className="w-5 h-5 text-white group-hover:text-black" />
          </button>

          {/* Next Video (smaller, right side) */}
          <div 
            className="hidden md:block relative w-48 lg:w-64 aspect-video rounded-lg overflow-hidden border border-[#152840] opacity-50 hover:opacity-80 cursor-pointer transition-all duration-300 flex-shrink-0"
            onClick={goToNext}
          >
            <img 
              src={getYouTubeThumbnail(nextItem.videoId)}
              alt={nextItem.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = `https://img.youtube.com/vi/${nextItem.videoId}/hqdefault.jpg`;
              }}
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <ChevronRight className="w-10 h-10 text-white/70" />
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {spotlightContent.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-[#FF6500] w-8' 
                  : 'bg-[#152840] hover:bg-[#FF6500]/50'
              }`}
              aria-label={`Go to spotlight ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 animate-fadeIn"
          onClick={closeModal}
        >
          <div 
            className="relative w-full max-w-6xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 p-2 text-white hover:text-[#FF6500] transition-colors duration-300"
              aria-label="Close video"
            >
              <X className="w-8 h-8" />
            </button>
            
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden shadow-2xl shadow-[#FF6500]/20">
              <iframe
                src={getYouTubeEmbed(selectedVideo.videoId)}
                title={selectedVideo.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            <div className="mt-6 text-center">
              <h3 className="text-2xl font-semibold text-white mb-2">{selectedVideo.title}</h3>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FeaturedSpotlightSection;
